// frontend/src/components/SuperAdminDashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { 
  CheckCircle, XCircle, AlertCircle, Clock, 
  FileText, MessageSquare, X, Download, Maximize2, Minimize2,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Globe, AlertTriangle,
  Search, Filter, RefreshCw, ThumbsUp, ThumbsDown,
  Building2, Mail, Phone, Calendar, Link, FileCheck, Loader2
} from 'lucide-react';

const SuperAdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null); // Tracks layout-level action loaders
  
  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewMessage, setReviewMessage] = useState('');
  
  // Approval confirmation modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalApplication, setApprovalApplication] = useState(null);
  const [verificationConfirmed, setVerificationConfirmed] = useState(false);
  
  // Document preview modal
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [, setDocumentType] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [documentMimeType, setDocumentMimeType] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  
  // PDF viewer state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);

  useEffect(() => {
    fetchApplications();
  }, []);

  const filterApplications = useCallback(() => {
    let filtered = [...applications];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status.toLowerCase() === statusFilter.toLowerCase());
    }
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        app.organizationName?.toLowerCase().includes(term) ||
        app.email?.toLowerCase().includes(term) ||
        app.subdomain?.toLowerCase().includes(term) ||
        app.applicationId?.toLowerCase().includes(term)
      );
    }
    
    setFilteredApplications(filtered);
  }, [applications, searchTerm, statusFilter]);
  
  useEffect(() => {
    filterApplications();
  }, [searchTerm, statusFilter, applications, filterApplications]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/application');
      const data = response.data?.data || response.data || [];
      setApplications(data);
      setError('');
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to fetch applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, message = null) => {
    try {
      setProcessingId(id);
      await api.post('/api/application/change-status', { 
        id, 
        status, 
        reviewMessage: message 
      });
      
      await fetchApplications();
      setShowReviewModal(false);
      setShowApprovalModal(false);
      setSelectedApplication(null);
      setApprovalApplication(null);
      setReviewMessage('');
      setVerificationConfirmed(false);
      
    } catch (err) {
      console.error(`Error updating application status:`, err);
      alert(err.response?.data?.message || 'Failed to update application status.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReview = (app) => {
    setSelectedApplication(app);
    setReviewMessage('');
    setShowReviewModal(true);
  };

  const submitReview = () => {
    if (!reviewMessage.trim()) {
      alert('Please enter a review message.');
      return;
    }
    updateStatus(selectedApplication._id, 'Review', reviewMessage);
  };

  const handleApproveClick = (app) => {
    setApprovalApplication(app);
    setVerificationConfirmed(false);
    setShowApprovalModal(true);
  };

  const handleVerificationConfirm = () => {
    setVerificationConfirmed(!verificationConfirmed);
  };

  const proceedWithApproval = () => {
    if (!verificationConfirmed) {
      alert('Please confirm that you have verified the subdomain');
      return;
    }
    updateStatus(approvalApplication._id, 'Approved');
  };

  const viewDocument = useCallback(async (application, docType, fileName) => {
    try {
      setDocumentLoading(true);
      
      const document = application.documents?.[docType];
      
      if (!document || !document.fileId) {
        throw new Error('Document not found');
      }
      
      setDocumentType(docType);
      setDocumentName(fileName || document.fileName || docType);
      
      let mimeType = document.mimeType;
      if (!mimeType && document.fileName) {
        const ext = document.fileName.split('.').pop().toLowerCase();
        const mimeTypes = {
          'pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp'
        };
        mimeType = mimeTypes[ext] || 'application/octet-stream';
      }
      setDocumentMimeType(mimeType);
      
      const fileKey = encodeURIComponent(document.fileId);
      const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/application/document/${fileKey}`;
      
      console.log('Fetching document:', { url, mimeType, fileName: document.fileName });
      
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      
      setDocumentUrl(objectUrl);
      setShowDocumentModal(true);
      setIsFullScreen(false);
      setPageNumber(1);
      setScale(1.0);
      setNumPages(null);
      setDocumentLoading(false);
      
    } catch (err) {
      setDocumentLoading(false);
      console.error('Error viewing document:', err);
      alert(`Failed to load document: ${err.message}`);
    }
  }, []);

  const closeDocumentModal = () => {
    if (documentUrl) {
      window.URL.revokeObjectURL(documentUrl);
    }
    setShowDocumentModal(false);
    setDocumentUrl('');
    setDocumentType('');
    setDocumentName('');
    setDocumentMimeType('');
    setIsFullScreen(false);
    setNumPages(null);
    setPageNumber(1);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Approved':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'Rejected':
        return <XCircle className="w-4 h-4 text-rose-600" />;
      case 'Review':
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      default:
        return <Clock className="w-4 h-4 text-blue-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Rejected':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'Review':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'Pending').length,
    approved: applications.filter(a => a.status === 'Approved').length,
    rejected: applications.filter(a => a.status === 'Rejected').length,
    review: applications.filter(a => a.status === 'Review').length,
  };

  const isPdf = documentMimeType === 'application/pdf' || 
                 documentName?.toLowerCase().endsWith('.pdf');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 text-slate-900 font-sans antialiased">
      {/* Sticky Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Super Admin Dashboard</h1>
            <p className="text-xs text-slate-500 mt-0.5">Review system enrollment configurations and tenant spaces</p>
          </div>
          <button
            onClick={fetchApplications}
            disabled={processingId !== null}
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-slate-200 rounded-lg shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all active:scale-[0.98]"
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${processingId ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Notification Alert */}
        {error && (
          <div className="mb-6 bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center justify-between shadow-sm animate-fade-in">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0" />
              <span className="text-sm font-medium text-rose-800">{error}</span>
            </div>
            <button 
              onClick={() => setError('')} 
              className="text-rose-500 hover:text-rose-700 p-1 hover:bg-rose-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Dynamic Metric Grid */}
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Inquiries', count: stats.total, color: 'text-slate-900', bg: 'bg-slate-100 text-slate-700', icon: Building2 },
            { label: 'Pending Steps', count: stats.pending, color: 'text-blue-700', bg: 'bg-blue-50 text-blue-700', icon: Clock },
            { label: 'Active Domains', count: stats.approved, color: 'text-emerald-700', bg: 'bg-emerald-50 text-emerald-700', icon: CheckCircle },
            { label: 'Returned Status', count: stats.review, color: 'text-amber-700', bg: 'bg-amber-50 text-amber-700', icon: MessageSquare },
            { label: 'Rejected Entries', count: stats.rejected, color: 'text-rose-700', bg: 'bg-rose-50 text-rose-700', icon: XCircle },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-sm hover:border-slate-300 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{card.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.bg}`}>
                  <card.icon className="w-4 h-4" />
                </div>
              </div>
              <p className={`text-2xl font-bold tracking-tight mt-2 ${card.color}`}>{card.count}</p>
            </div>
          ))}
        </section>

        {/* Advanced Segment Filter Panel */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-8 flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by organization name, email domain, or assigned subdomain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
            />
          </div>
          <div className="md:w-56 relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none text-slate-700 font-medium cursor-pointer"
            >
              <option value="all">Show All Records</option>
              <option value="pending">Status: Pending</option>
              <option value="approved">Status: Approved</option>
              <option value="rejected">Status: Rejected</option>
              <option value="review">Status: In Review</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none border-l border-slate-200 pl-2">
              <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-slate-500" />
            </div>
          </div>
        </section>

        {/* Data Output Matrix */}
        {filteredApplications.length > 0 ? (
          <section className="space-y-4">
            {filteredApplications.map((app) => {
              const isAppProcessing = processingId === app._id;
              return (
                <div
                  key={app._id}
                  className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover:border-slate-300 transition-all duration-200"
                >
                  <div className="p-6">
                    {/* Entry Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-100 mb-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-slate-900">{app.organizationName}</h3>
                          <span className="text-xs text-slate-400 font-mono bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded">
                            {app.applicationId}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1 font-medium text-blue-600">
                            <Link className="w-3 h-3" />
                            {app.subdomain}.truyon.com
                          </span>
                        </div>
                      </div>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border shadow-sm ${getStatusColor(app.status)}`}>
                        {getStatusIcon(app.status)}
                        {app.status}
                      </div>
                    </div>

                    {/* Metadata Coordinates */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 text-sm bg-slate-50/70 p-4 rounded-xl border border-slate-100">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{app.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span>{app.phone || 'No phone supplied'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span>
                          {new Date(app.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Physical Destination Registry */}
                    {app.address && (
                      <div className="mb-4 text-xs text-slate-600 flex gap-1.5 items-start">
                        <span className="font-semibold text-slate-500 uppercase tracking-wide">Registered Site:</span>
                        <p className="bg-slate-50 px-2 py-1 rounded border border-slate-100 flex-1">{app.address}</p>
                      </div>
                    )}

                    {/* Encapsulated Document Actions */}
                    <div className="mb-5 bg-slate-50/30 p-3 rounded-lg border border-slate-100">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Verification Vault</h4>
                      <div className="flex flex-wrap gap-2.5">
                        {app.documents?.addressProof && (
                          <button
                            onClick={() => viewDocument(app, 'addressProof', app.documents.addressProof.fileName || 'Address Proof')}
                            disabled={documentLoading}
                            className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors text-xs font-medium shadow-sm"
                          >
                            <FileText className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                            Review Address Proof
                          </button>
                        )}
                        {app.documents?.institutionProof && (
                          <button
                            onClick={() => viewDocument(app, 'institutionProof', app.documents.institutionProof.fileName || 'Institution Proof')}
                            disabled={documentLoading}
                            className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors text-xs font-medium shadow-sm"
                          >
                            <FileCheck className="w-3.5 h-3.5 mr-1.5 text-purple-500" />
                            Review Institution Proof
                          </button>
                        )}
                        {!app.documents?.addressProof && !app.documents?.institutionProof && (
                          <span className="text-xs text-slate-400 italic">No verification documents attached to this record.</span>
                        )}
                      </div>
                    </div>

                    {/* Escalation Notes Feedback Box */}
                    {app.reviewComments && (
                      <div className="mb-5 p-3.5 bg-amber-50/60 border border-amber-200/70 rounded-xl">
                        <div className="flex items-start gap-2.5">
                          <MessageSquare className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="space-y-0.5">
                            <p className="text-xs font-bold text-amber-900">Active Review Instructions:</p>
                            <p className="text-xs text-amber-800 leading-relaxed">{app.reviewComments}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Task Execution Bar */}
                    <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-slate-100">
                      <button
                        onClick={() => handleReview(app)}
                        disabled={processingId !== null}
                        className="inline-flex items-center px-3 py-2 bg-amber-500 text-white text-xs font-semibold rounded-lg hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/20 shadow-sm transition-all disabled:opacity-50 active:scale-[0.98]"
                      >
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                        Request Modifications
                      </button>
                      <button
                        onClick={() => updateStatus(app._id, 'Rejected')}
                        disabled={processingId !== null}
                        className="inline-flex items-center px-3 py-2 bg-rose-600 text-white text-xs font-semibold rounded-lg hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20 shadow-sm transition-all disabled:opacity-50 active:scale-[0.98]"
                      >
                        <ThumbsDown className="w-3.5 h-3.5 mr-1.5" />
                        Reject Entry
                      </button>
                      <button
                        onClick={() => handleApproveClick(app)}
                        disabled={processingId !== null}
                        className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm transition-all disabled:opacity-50 active:scale-[0.98]"
                      >
                        {isAppProcessing ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <ThumbsUp className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Finalize Approval
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center max-w-xl mx-auto shadow-sm">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4 border border-slate-200">
              <Building2 className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-800">No organizational applications match</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your text search terms or changing your operational status dropdown filter criteria.' 
                : 'Any incoming application packets awaiting baseline configuration steps will populate here dynamically.'}
            </p>
          </div>
        )}
      </main>

      {/* Action Dialog: Modification Message Router */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-slate-200/60 transform transition-all">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Return for Modification</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Provide explicit steps detailing what adjustments <span className="font-semibold text-slate-700">{selectedApplication?.organizationName}</span> needs to complete before resubmission.
            </p>
            <textarea
              value={reviewMessage}
              onChange={(e) => setReviewMessage(e.target.value)}
              rows="4"
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 placeholder:text-slate-400"
              placeholder="E.g., Your institution proof upload was blurry. Please supply a clear copy of your regional business charter..."
              autoFocus
            />
            <div className="flex justify-end space-x-2 mt-5">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={submitReview}
                disabled={processingId !== null}
                className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition-all shadow-sm flex items-center"
              >
                {processingId ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Dispatch Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Dialog: Operational Tenant Activation Validation Gate */}
      {showApprovalModal && approvalApplication && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-slate-200/60 transform transition-all">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Tenant Domain Sign-off</h3>
            </div>
            
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Verify that the specific sub-network infrastructure context parameters map cleanly to external system gateways.
            </p>
            
            <div className="bg-slate-50 p-4 rounded-xl mb-4 border border-slate-100 text-center">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Target Application Domain Space</span>
              <p className="text-base font-bold text-blue-600 font-mono tracking-tight">{approvalApplication.subdomain}.truyon.com</p>
            </div>

            <div className="space-y-4 mb-5">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={verificationConfirmed}
                  onChange={handleVerificationConfirm}
                  className="mt-0.5 h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500/20 cursor-pointer"
                />
                <span className="text-xs text-slate-600 select-none group-hover:text-slate-900 transition-colors leading-relaxed">
                  I confirm that this subdomain allocation maps cleanly onto Vercel record rules and does not create routing block conflicts.
                </span>
              </label>
              
              <div className="bg-amber-50/60 p-3 rounded-xl border border-amber-200/60 flex gap-2.5 items-start">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-amber-800 leading-relaxed">
                  Completing this cycle initializes background automated tenant workspace builds immediately. Verify infrastructure integrity before confirmation.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel Step
              </button>
              <button
                onClick={proceedWithApproval}
                disabled={!verificationConfirmed || processingId !== null}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm flex items-center"
              >
                {processingId ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Confirm Workspace Build
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Specialized Document Preview Sheet */}
      {showDocumentModal && (
        <div 
          className={`fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-sm transition-all ${
            isFullScreen ? 'p-0' : 'p-6'
          }`}
          onClick={closeDocumentModal}
        >
          <div 
            className={`bg-white shadow-2xl overflow-hidden flex flex-col transition-all ${
              isFullScreen 
                ? 'w-full h-full rounded-none' 
                : 'max-w-5xl w-full h-[85vh] rounded-xl border border-slate-800/40'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Control Strip */}
            <div className="px-4 h-14 border-b border-slate-200 flex justify-between items-center bg-slate-50 flex-shrink-0">
              <div className="flex items-center gap-2 max-w-[60%]">
                <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-slate-800 truncate">
                  {documentName}
                </h3>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={toggleFullScreen}
                  className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg transition-colors"
                  title={isFullScreen ? 'Minimize view frame' : 'Maximize view frame'}
                >
                  {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <a
                  href={documentUrl}
                  download={documentName}
                  className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg transition-colors flex items-center justify-center"
                  title="Save local backup"
                >
                  <Download className="w-4 h-4" />
                </a>
                <div className="w-px h-4 bg-slate-200 mx-1" />
                <button
                  onClick={closeDocumentModal}
                  className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* View Canvas */}
            <div className="flex-1 overflow-auto bg-slate-100 p-4 flex flex-col items-center">
              {documentLoading ? (
                <div className="m-auto text-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
                  <p className="mt-2 text-xs font-medium text-slate-500">Unpacking file resource stream...</p>
                </div>
              ) : documentUrl && (
                <>
                  {isPdf ? (
                    <div className="w-full h-full flex flex-col items-center">
                      {/* Document Frame Control Controls */}
                      <div className="bg-white px-4 py-2 mb-3 rounded-lg shadow-sm border border-slate-200 flex items-center justify-between gap-6 w-full max-w-md flex-shrink-0">
                        <div className="flex items-center space-x-1.5">
                          <button
                            onClick={() => setScale(s => Math.max(0.6, s - 0.1))}
                            className="p-1 text-slate-500 hover:bg-slate-100 rounded"
                          >
                            <ZoomOut className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-xs font-mono font-bold text-slate-600 min-w-[35px] text-center">{Math.round(scale * 100)}%</span>
                          <button
                            onClick={() => setScale(s => Math.min(1.8, s + 0.1))}
                            className="p-1 text-slate-500 hover:bg-slate-100 rounded"
                          >
                            <ZoomIn className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                            disabled={pageNumber <= 1}
                            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="text-xs font-medium text-slate-600">
                            Page {pageNumber} / {numPages || '1'}
                          </span>
                          <button
                            onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))}
                            disabled={pageNumber >= (numPages || 1)}
                            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="w-full flex-1 rounded-lg shadow-inner overflow-hidden bg-slate-300">
                        <iframe
                          src={`${documentUrl}#toolbar=0&navpanes=0&view=FitH&zoom=${scale * 100}`}
                          className="w-full h-full border-0"
                          title="PDF Core Preview Asset"
                        />
                      </div>
                    </div>
                  ) : (
                    /* Graphical Raster Stream Viewer */
                    <div className="m-auto relative max-w-full max-h-full flex items-center justify-center">
                      <img
                        src={documentUrl}
                        alt="Verification Asset File"
                        className="max-w-full max-h-full object-contain rounded-lg shadow-md transition-transform duration-150"
                        style={{ 
                          maxHeight: isFullScreen ? 'calc(100vh - 90px)' : 'calc(85vh - 110px)',
                          transform: `scale(${scale})`
                        }}
                      />
                      <div className="absolute bottom-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-md border border-slate-200/80 px-2.5 py-1 flex items-center space-x-2">
                        <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))} className="p-1 text-slate-500 hover:bg-slate-100 rounded">
                          <ZoomOut className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-[11px] font-mono font-bold text-slate-600">{Math.round(scale * 100)}%</span>
                        <button onClick={() => setScale(s => Math.min(2, s + 0.1))} className="p-1 text-slate-500 hover:bg-slate-100 rounded">
                          <ZoomIn className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminApplications;